import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import {
  LayoutDashboard, ShoppingCart, Users, Truck, Wallet, TrendingUp,
  Sparkles, FileText, Bell, UserCog, Moon, Sun, LogOut, Menu, X,
  UtensilsCrossed, ChefHat, Armchair, CreditCard,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { QuickAddButton } from "@/components/QuickAdd";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["owner", "manager", "employee"] },
  { to: "/orders", label: "Tables & Orders", icon: Armchair, roles: ["owner", "manager", "employee"] },
  { to: "/kitchen", label: "Kitchen (KOT)", icon: ChefHat, roles: ["owner", "manager", "employee"] },
  { to: "/menu", label: "Menu", icon: UtensilsCrossed, roles: ["owner", "manager"] },
  { to: "/billing", label: "Billing", icon: ShoppingCart, roles: ["owner", "manager", "employee"] },
  { to: "/customers", label: "Customers", icon: Users, roles: ["owner", "manager"] },
  { to: "/suppliers", label: "Vendors", icon: Truck, roles: ["owner", "manager"] },
  { to: "/expenses", label: "Expenses", icon: Wallet, roles: ["owner", "manager"] },
  { to: "/cashflow", label: "Cash Flow", icon: TrendingUp, roles: ["owner"] },
  { to: "/advisor", label: "AI Advisor", icon: Sparkles, roles: ["owner", "manager"], highlight: true },
  { to: "/reports", label: "Reports", icon: FileText, roles: ["owner", "manager"] },
  { to: "/alerts", label: "Alerts", icon: Bell, roles: ["owner", "manager"] },
  { to: "/team", label: "Team & Audit", icon: UserCog, roles: ["owner"] },
  { to: "/subscription", label: "Subscription", icon: CreditCard, roles: ["owner"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const role = user?.role || "owner";
  const items = NAV.filter((n) => n.roles.includes(role));

  const SidebarInner = () => (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 px-6 h-16 border-b border-border">
        <div className="grid place-items-center h-9 w-9 rounded-lg bg-gradient-to-br from-amber-300 to-yellow-600 text-neutral-900">
          <UtensilsCrossed className="h-5 w-5" />
        </div>
        <div className="leading-tight">
          <div className="font-heading font-bold tracking-tight text-lg">KHEVORA</div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground -mt-0.5">Restaurant OS</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={`nav-${n.to.slice(1)}`}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            <n.icon className="h-[18px] w-[18px] shrink-0" />
            <span>{n.label}</span>
            {n.highlight && (
              <span className="ml-auto text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/15 text-primary group-[.bg-primary]:bg-white/20 group-[.bg-primary]:text-white">
                AI
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-3">
        <div className="flex items-center gap-3 rounded-lg px-2 py-2">
          <Avatar className="h-9 w-9">
            {user?.picture && <AvatarImage src={user.picture} />}
            <AvatarFallback className="bg-primary/15 text-primary text-xs font-bold">
              {user?.name?.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">{user?.name}</div>
            <div className="truncate text-xs text-muted-foreground capitalize">{role}</div>
          </div>
          <button data-testid="logout-btn" onClick={logout} className="text-muted-foreground hover:text-destructive transition-colors">
            <LogOut className="h-[18px] w-[18px]" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative min-h-screen">
      {/* Premium emerald + gold ambience behind all content */}
      <div aria-hidden className="fixed inset-0 -z-10" style={{ background: "radial-gradient(circle at 85% 12%, hsl(40 70% 50% / 0.12) 0, transparent 45%), radial-gradient(circle at 10% 90%, hsl(158 60% 32% / 0.14) 0, transparent 48%), hsl(var(--background))" }} />
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-screen w-64 flex-col border-r border-border bg-card z-40">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-card border-r border-border animate-fade-up">
            <SidebarInner />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 h-16 flex items-center gap-3 border-b border-border glass px-4 md:px-8">
          <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1" />
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate("/advisor")}
            className="hidden sm:inline-flex"
            data-testid="ask-ai-header-btn"
          >
            <Sparkles className="h-4 w-4 text-primary" />
          </Button>
          <button
            data-testid="theme-toggle"
            onClick={toggle}
            className="grid place-items-center h-9 w-9 rounded-lg border border-border hover:bg-secondary transition-colors"
          >
            {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
          </button>
        </header>
        <main className="p-4 md:p-8 max-w-[1600px] mx-auto">{children}</main>
      </div>
    </div>
  );
}
